import React from "react";
import { Box, IconButton, MenuItem, useMediaQuery, useTheme } from "@mui/material";
import {
  ChevronRight as ChevronRightIcon,
  ExpandMore as ExpandMoreIcon,
  EditOutlined as EditIcon,
  PlaceOutlined as PlaceIcon,
  BusinessOutlined as SubLocationIcon,
  PrecisionManufacturing as MachineIcon,
} from "@mui/icons-material";
import { dt, accentGradientLight } from "../../../../shared/themes/redesignTokens";
import TableActionButton from "../../../../shared/components/tables/TableActionButton";
import type { Location } from "./locationTypes";

// ═══════════════════════════════════════════════════════════════
//  PROPS
// ═══════════════════════════════════════════════════════════════

interface LocationRowProps {
  location: Location;
  isChild?: boolean;
  expanded?: boolean;
  onToggleExpand?: () => void;
  onEdit: (location: Location) => void;
  onSetDefault?: (location: Location) => void;
  onDisable?: (location: Location) => void;
}

// ═══════════════════════════════════════════════════════════════
//  COMPONENT
// ═══════════════════════════════════════════════════════════════

const LocationRow: React.FC<LocationRowProps> = ({
  location,
  isChild = false,
  expanded,
  onToggleExpand,
  onEdit,
  onSetDefault,
  onDisable,
}) => {
  const theme = useTheme();
  const isPhone = useMediaQuery("(max-width:768px)");
  const hasChildren = location.childLocations && location.childLocations.length > 0;

  return (
    <Box
      sx={{
        display: "flex",
        alignItems: "center",
        gap: isPhone ? "8px" : "12px",
        px: isChild ? (isPhone ? "12px" : "16px") : (isPhone ? "12px" : "20px"),
        py: isPhone ? "10px" : "14px",
        borderTop: isChild ? `1px solid ${dt.rowBorder}` : "none",
        pl: isChild ? (isPhone ? "40px" : "56px") : undefined,
        transition: "background-color 0.15s ease",
        "&:hover": {
          backgroundColor: dt.rowHover,
        },
      }}
    >
      {/* Expand/collapse chevron or sub-location icon */}
      {!isChild && hasChildren ? (
        <IconButton
          size="small"
          onClick={onToggleExpand}
          sx={{
            width: 28,
            height: 28,
            flexShrink: 0,
            color: dt.textMuted,
            transition: "all 0.2s ease",
            "&:hover": { color: dt.blue, backgroundColor: dt.blueGlow },
          }}
        >
          {expanded ? (
            <ExpandMoreIcon sx={{ fontSize: 18 }} />
          ) : (
            <ChevronRightIcon sx={{ fontSize: 18 }} />
          )}
        </IconButton>
      ) : !isChild ? (
        <Box sx={{ width: 28, flexShrink: 0 }} />
      ) : null}

      {/* Location icon */}
      <Box
        sx={{
          width: 36,
          height: 36,
          borderRadius: dt.radiusSm,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          flexShrink: 0,
          backgroundColor: isChild ? dt.inputBg : dt.blueSoft,
          color: isChild ? dt.textMuted : dt.blue,
        }}
      >
        {isChild ? (
          <SubLocationIcon sx={{ fontSize: 18 }} />
        ) : (
          <PlaceIcon sx={{ fontSize: 18 }} />
        )}
      </Box>

      {/* Name + address/description */}
      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Box
          sx={{
            fontSize: isPhone ? "13px" : "14px",
            fontWeight: 600,
            color: dt.textPrimary,
            overflow: "hidden",
            textOverflow: "ellipsis",
            whiteSpace: "nowrap",
          }}
        >
          {location.name}
        </Box>
        {(location.description || location.city) && (
          <Box
            sx={{
              fontSize: "12px",
              color: dt.textMuted,
              mt: "2px",
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
            }}
          >
            {location.description ||
              [location.addressLine1, location.zipCode, location.city, location.countryName]
                .filter(Boolean)
                .join(", ")}
          </Box>
        )}
      </Box>

      {/* Badges area */}
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          gap: isPhone ? "6px" : "10px",
          flexShrink: 0,
        }}
      >
        {/* Machine count badge */}
        {location.machineCount != null && location.machineCount > 0 && (
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: "4px",
              px: "8px",
              py: "3px",
              borderRadius: dt.radiusSm,
              backgroundColor: dt.inputBg,
              fontSize: "12px",
              fontWeight: 500,
              fontFamily: dt.fontMono,
              color: dt.textSecondary,
              whiteSpace: "nowrap",
            }}
          >
            <MachineIcon sx={{ fontSize: 13, color: dt.textMuted }} />
            {location.machineCount} machine{location.machineCount !== 1 ? "s" : ""}
          </Box>
        )}

        {/* Sub-locations count (parent only) */}
        {!isChild && hasChildren && (
          <Box
            sx={{
              px: "8px",
              py: "3px",
              borderRadius: dt.radiusSm,
              fontSize: "12px",
              fontWeight: 500,
              fontFamily: dt.fontMono,
              color: dt.blue,
              backgroundColor: dt.blueSoft,
              whiteSpace: "nowrap",
              display: isPhone ? "none" : "block",
            }}
          >
            {location.childLocations.length} sub-location
            {location.childLocations.length !== 1 ? "s" : ""}
          </Box>
        )}

        {/* DEFAULT badge */}
        {location.isDefault && (
          <Box
            sx={{
              px: "10px",
              py: "3px",
              borderRadius: dt.radiusSm,
              fontSize: "11px",
              fontWeight: 700,
              fontFamily: dt.fontMono,
              letterSpacing: "0.5px",
              color: dt.textSecondary,
              border: `1px solid ${dt.cardBorder}`,
              whiteSpace: "nowrap",
              display: isPhone ? "none" : "block",
            }}
          >
            DEFAULT
          </Box>
        )}

        {/* Edit icon button */}
        <IconButton
          size="small"
          onClick={() => onEdit(location)}
          sx={{
            width: 30,
            height: 30,
            color: dt.textMuted,
            transition: "all 0.2s ease",
            "&:hover": {
              color: dt.blue,
              backgroundColor: dt.blueGlow,
            },
          }}
        >
          <EditIcon sx={{ fontSize: 16 }} />
        </IconButton>

        {/* 3-dot menu */}
        <TableActionButton color={dt.textMuted}>
          {!isChild && !location.isDefault && onSetDefault && (
            <MenuItem
              onClick={() => onSetDefault(location)}
              sx={{ fontFamily: dt.font, fontSize: "13px" }}
            >
              Set as Default
            </MenuItem>
          )}
          {onDisable && (
            <MenuItem
              onClick={() => onDisable(location)}
              sx={{ fontFamily: dt.font, fontSize: "13px", color: "#C0392B" }}
            >
              {location.isDisabled ? "Enable" : "Disable"}
            </MenuItem>
          )}
        </TableActionButton>
      </Box>
    </Box>
  );
};

export default LocationRow;
